import { useEffect } from "react";
import * as React from "react";
import { Typography, Radio, Form, Input, Select, Row, Col, DatePicker } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';

import IconButton from "../../ui/IconButton";
import FormHeader from "../FormHeader";
import { DateValue, CurrencyValue, DateSelectionType, CurrencySelectionType, EquityPlaceholderName } from "../../../types/newLinkBuilder";
import useFetchCurrencies from "../../../utils/useFetchCurrencies";

interface DateAndCurrencyFormProps {
    dateValue: DateValue;
    setDateValue: (value: DateValue) => void;
    currencyValue: CurrencyValue;
    setCurrencyValue: (value: CurrencyValue) => void;
    handlePlaceholder: (name: EquityPlaceholderName) => void;
    isVolumeAndTrendingCurrencySelected: boolean;
    showTimePeriod: boolean;
}

const DateAndCurrencyForm: React.FC<DateAndCurrencyFormProps> = ({ dateValue, setDateValue, currencyValue, setCurrencyValue, handlePlaceholder, isVolumeAndTrendingCurrencySelected, showTimePeriod }) => {
    const { currencyOptions, fetchCurrencies } = useFetchCurrencies();

    useEffect(() => {
        fetchCurrencies();
    }, []);

    const handleAsOfDateChange = (field: keyof DateValue, value: string) => {
        setDateValue({
            ...dateValue,
            [field]: value
        })
    }

    const handleCurrencyTypeChange = (field: keyof CurrencyValue, value: string) => {
        setCurrencyValue({
            ...currencyValue,
            [field]: value
        })
    }
    return (
        <div>
            <FormHeader title={`${showTimePeriod ? "Time Period" : `Date ${isVolumeAndTrendingCurrencySelected ? "" : "and Currency"}`}`} />
            {!showTimePeriod && (
                <>
                    {!isVolumeAndTrendingCurrencySelected && (
                        <Typography.Title level={5} style={{ textAlign: 'left' }}>
                            <u>As-of-date</u>
                        </Typography.Title>
                    )}
                    <Form.Item
                        name={"asOfDate"}
                    >
                        <Radio.Group
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                            }}
                            options={[{
                                value: DateSelectionType.Date,
                                label: (
                                    <Row
                                        align={"middle"}
                                    >
                                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <span>Date</span>
                                        </Col>

                                        <Col span={18} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <DatePicker
                                                format="MM/DD/YYYY"
                                                placeholder="Select a date"
                                                maxDate={dayjs()}
                                                disabled={dateValue.selected === DateSelectionType.CellReference}
                                                style={{ width: "100%" }}
                                                value={dateValue.date ? dayjs(dateValue.date) : null}
                                                onChange={(value) => handleAsOfDateChange("date", value ? dayjs(value).toISOString() : "")}
                                                onClick={(e) => e.preventDefault()}
                                            />
                                        </Col>
                                    </Row>
                                )
                            }, {
                                value: DateSelectionType.CellReference,
                                label: (
                                    <Row
                                        align={"middle"}
                                    >
                                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <span>Cell Reference</span>
                                        </Col>

                                        <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <Input
                                                placeholder="Cell Reference"
                                                disabled={dateValue.selected === DateSelectionType.Date}
                                                value={dateValue.dateCellReference}
                                                onChange={(e) => handleAsOfDateChange("dateCellReference", e.target.value)}
                                                onClick={(e) => e.preventDefault()}
                                            />
                                        </Col>

                                        <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                            <IconButton
                                                disabled={dateValue.selected === DateSelectionType.Date}
                                                icon={<UploadOutlined />}
                                                onClick={() => handlePlaceholder(EquityPlaceholderName.DateCellReference)}
                                            />
                                        </Col>
                                    </Row>
                                )
                            }
                            ]}
                        />
                    </Form.Item>    
                </>
            )}

            {showTimePeriod && (
                <>
                    <Form.Item name={"timePeriod"}>
                        <Radio.Group
                            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
                            options={[{
                                value: DateSelectionType.Date,
                                label: (
                                    <Row align={"middle"}>
                                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <span>Select Dates</span>
                                        </Col>
                                        <Col span={18} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <DatePicker.RangePicker
                                                format="MM/DD/YYYY"
                                                maxDate={dayjs()}
                                                disabled={dateValue.selected === DateSelectionType.CellReference}
                                                style={{ width: "100%" }}
                                                value={dateValue.beginDate && dateValue.endDate ? [dayjs(dateValue.beginDate), dayjs(dateValue.endDate)] : null}
                                                onChange={(values) => {
                                                    const [start, end] = (values || []) as any;
                                                    setDateValue({
                                                        ...dateValue,
                                                        beginDate: start ? dayjs(start).toISOString() : "",
                                                        endDate: end ? dayjs(end).toISOString() : "",
                                                    });
                                                }}
                                                onClick={(e) => e.preventDefault()}
                                            />
                                        </Col>
                                    </Row>
                                )
                            }, {
                                value: DateSelectionType.CellReference,
                                label: (
                                    <Row align={"middle"}>
                                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <span>Cell Reference</span>
                                        </Col>
                                        <Col span={18} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                                                <Row>
                                                    <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                                        <span>- Beginning Date</span>
                                                    </Col>
                                                    <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                                        <Input
                                                            placeholder="Beginning Date Cell Reference"
                                                            disabled={dateValue.selected === DateSelectionType.Date}
                                                            value={dateValue.beginDateCellReference}
                                                            onChange={(e) => handleAsOfDateChange("beginDateCellReference", e.target.value)}
                                                        />
                                                    </Col>
                                                    <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                                        <IconButton
                                                            disabled={dateValue.selected === DateSelectionType.Date}
                                                            icon={<UploadOutlined />}
                                                            onClick={() => handlePlaceholder(EquityPlaceholderName.BeginningDateCellReference)}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                                        <span>- Ending Date</span>
                                                    </Col>
                                                    <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                                        <Input
                                                            placeholder="Ending Date Cell Reference"
                                                            disabled={dateValue.selected === DateSelectionType.Date}
                                                            value={dateValue.endDateCellReference}
                                                            onChange={(e) => handleAsOfDateChange("endDateCellReference", e.target.value)}
                                                        />
                                                    </Col>
                                                    <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                                        <IconButton
                                                            disabled={dateValue.selected === DateSelectionType.Date}
                                                            icon={<UploadOutlined />}
                                                            onClick={() => handlePlaceholder(EquityPlaceholderName.EndingDateCellReference)}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Col>
                                    </Row>
                                )
                            }]}
                        />
                    </Form.Item>
                </>
            )}

            {!isVolumeAndTrendingCurrencySelected && (
                <>
                    <Typography.Title level={5} style={{ textAlign: 'left' }}>
                        <u>Currency Type</u>
                    </Typography.Title>
                    <Form.Item
                        name={"currencyType"}
                    >
                        <Radio.Group
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                            }}
                            options={[{
                                value: CurrencySelectionType.Value,
                                label: (
                                    <Row
                                        align={"middle"}
                                    >
                                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <span>Value</span>
                                        </Col>
                                        <Col span={18} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <Select
                                                placeholder="Currency"
                                                disabled={currencyValue.selected === CurrencySelectionType.CellReference}
                                                value={currencyValue.currency}
                                                onChange={(value) => handleCurrencyTypeChange("currency", value)}
                                                style={{ width: "100%" }}
                                                onClick={(e) => e.preventDefault()}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={currencyOptions}
                                            />
                                        </Col>
                                    </Row>
                                )
                            }, {
                                value: CurrencySelectionType.CellReference,
                                label: (
                                    <Row
                                        align={"middle"}
                                    >
                                        <Col span={6} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <span>Cell Reference</span>
                                        </Col>
                                        <Col span={16} style={{ display: 'flex', justifyContent: 'start' }}>
                                            <Input
                                                placeholder="Currency Type"
                                                disabled={currencyValue.selected === CurrencySelectionType.Value}
                                                value={currencyValue.currencyCellReference}
                                                onChange={(e) => handleCurrencyTypeChange("currencyCellReference", e.target.value)}
                                            />
                                        </Col>
                                        <Col span={2} style={{ display: 'flex', justifyContent: 'end' }}>
                                            <IconButton
                                                disabled={currencyValue.selected === CurrencySelectionType.Value}
                                                icon={<UploadOutlined />}
                                                onClick={() => handlePlaceholder(EquityPlaceholderName.CurrencyCellReference)}
                                            />
                                        </Col>
                                    </Row>
                                )
                            }
                            ]}
                        />
                    </Form.Item>
                </>
            )}
        </div>
    )
}

export default DateAndCurrencyForm; 